import { useState, useEffect, useCallback, useRef } from 'react';
import { hayVersionNueva, versionActual } from '../utils/pwa';

/**
 * Vigila si hay una versión más nueva publicada.
 *
 * Cuándo consulta, y por qué así:
 *
 *  - **Al abrir**, con unos segundos de retraso, para no competir con la carga
 *    del catálogo, que es lo que el usuario está esperando ver.
 *  - **Al volver a la app.** Es el caso importante en un teléfono: la PWA queda
 *    en segundo plano días entre usos y no se recarga sola. Sin esto, alguien
 *    podría estar semanas con una versión vieja sin enterarse.
 *  - **Al volver a la ventana**, que NO es lo mismo. `visibilitychange` sólo
 *    salta cuando la pestaña queda oculta —se cambia de pestaña, se minimiza el
 *    navegador—. En escritorio lo habitual es dejar CholloMan visible en una
 *    ventana y trabajar en otra aplicación: ahí la pestaña nunca se oculta, el
 *    evento nunca se dispara y el único chequeo que quedaba era el periódico de
 *    media hora. Por eso también se escucha `focus`.
 *  - **Cada tanto**, para las pestañas de escritorio que quedan abiertas.
 *
 * Cada versión se avisa una sola vez: si el usuario cierra el aviso, no vuelve
 * a aparecer hasta que se publique otra. Un modal que reaparece cada vez que
 * cambiás de pestaña deja de ser un aviso y pasa a ser una molestia.
 */

const ESPERA_INICIAL_MS = 4000;
const INTERVALO_MS = 30 * 60 * 1000;   // media hora

/**
 * Tiempo mínimo entre dos consultas disparadas por volver a la app.
 *
 * `focus` y `visibilitychange` llegan juntos al volver de otra pestaña, y
 * `focus` además salta con cada clic en la ventana. Sin freno, trabajar con dos
 * ventanas abiertas sería una petición por cada ida y vuelta.
 *
 * Diez segundos y no un minuto: el freno está para agrupar una ráfaga de
 * eventos, no para decidir cada cuánto se comprueba. Con un minuto, volver a la
 * ventana poco después del chequeo inicial no comprobaba nada y el aviso se
 * retrasaba sin motivo —se detectó probando justo ese caso—. La consulta es un
 * JSON de 60 bytes: agrupar la ráfaga alcanza.
 */
const MINIMO_ENTRE_CHEQUEOS_MS = 10 * 1000;

export const useActualizacion = () => {
  const [disponible, setDisponible] = useState(null);
  const avisadas = useRef(new Set());
  const consultando = useRef(false);
  const ultimoChequeo = useRef(0);

  const revisar = useCallback(async ({ forzar = true } = {}) => {
    if (consultando.current) return;
    // Los chequeos automáticos respetan el intervalo mínimo; los que pide el
    // usuario explícitamente (botón «buscar actualizaciones») no.
    if (!forzar && Date.now() - ultimoChequeo.current < MINIMO_ENTRE_CHEQUEOS_MS) return;
    consultando.current = true;
    ultimoChequeo.current = Date.now();
    try {
      const nueva = await hayVersionNueva();
      if (nueva && !avisadas.current.has(nueva.version)) {
        avisadas.current.add(nueva.version);
        setDisponible(nueva);
      }
    } finally {
      consultando.current = false;
    }
  }, []);

  useEffect(() => {
    const inicial = setTimeout(() => revisar(), ESPERA_INICIAL_MS);
    const periodico = setInterval(() => revisar(), INTERVALO_MS);

    // `forzar: false` para que los dos eventos, que suelen llegar juntos, no
    // se traduzcan en dos peticiones.
    const alVolver = () => {
      if (document.visibilityState === 'visible') revisar({ forzar: false });
    };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);

    return () => {
      clearTimeout(inicial);
      clearInterval(periodico);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
    };
  }, [revisar]);

  /** Cierra el aviso sin actualizar. No vuelve a salir por esta misma versión. */
  const descartar = useCallback(() => setDisponible(null), []);

  return {
    /** Datos de la versión nueva, o null si no hay ninguna pendiente de avisar. */
    disponible,
    /** La versión que está corriendo. */
    actual: versionActual(),
    descartar,
    revisar,
  };
};
